import { ACCESS_LEVELS_DICT } from '@/lib/Access.js'
import * as Sentry from '@sentry/browser'
import {
  cloneDeep,
  each,
  find,
  isArray,
  keyBy,
  union,
  unionBy,
  keys,
  pickBy,
  isEmpty
} from 'lodash-es'
import {
  updateAccessLevel,
  deleteBoard,
  deleteMainBoard,
  getBoard,
  getBoards,
  getBoardMapping,
  updateBoard
} from '@/services/boards-api.service.js'
import { exemptMappingRequirement } from '@/lib/mapping.js'
import { logger } from '@/utils/logger'

let activeFetchAllBoards = null
let fetchAllBoardsGeneration = 0

const getDefaultState = () => {
  return {
    boards: [],
    fetchCount: 0,
    hasLoadedBoards: false,
    isFetching: false,
    boardMappings: {}
  }
}

const state = getDefaultState()

const mutations = {
  setBoards: (_state, boards) => {
    _state.boards = boards
    _state.fetchCount++
  },

  setBoardsLoaded: (_state, hasLoadedBoards) => {
    _state.hasLoadedBoards = hasLoadedBoards
  },

  reset(_state) {
    // Merge rather than replace so we don't lose observers
    // https://github.com/vuejs/vuex/issues/1118
    Object.assign(_state, getDefaultState())
  },

  updateBoard(_state, { boardId, key, value }) {
    const clone = cloneDeep(_state.boards)
    each(clone, (obj) => {
      if (obj.boardId === boardId) {
        obj[key] = value
      }
    })
    _state.boards = clone
  },

  updateBoardMapping(_state, { boardId, mapping }) {
    _state.boardMappings[boardId] = mapping
  },

  setIsFetching(_state, isFetching) {
    _state.isFetching = isFetching
  },

  SET_COLOUR(_state, { boardId, content, filter, colour }) {
    const board = _state.boards.find((board) => board.boardId === boardId)
    board.theme = board.theme ?? {}
    board.theme[content] = board.theme[content] ?? {}
    board.theme[content][filter] = colour
  },
  SET_THEME(_state, { boardId, theme }) {
    if (theme) {
      _state.boards.find((board) => board.boardId === boardId).theme = theme
    }
  }
}

const getters = {
  isNestle: (_state) => (boardId) => {
    // bd_cC3vV9IHqFI1McO4pMDKtB is large testing account
    if (['bd_hyICp4OCdoqRcl3jBDWUu', 'bd_cC3vV9IHqFI1McO4pMDKtB'].includes(boardId)) return true
    const board = _state.boards.find((board) => board.boardId === boardId)
    return (
      board &&
      ['bd_hyICp4OCdoqRcl3jBDWUu', 'bd_cC3vV9IHqFI1McO4pMDKtB'].includes(board.originalBoardId)
    )
  },
  getBoard: (_state) => (boardId) =>
    _state.boards?.find((board) => board.boardId === boardId) || null,
  getBoardMapping: (_state) => (boardId) => _state.boardMappings[boardId],
  boardMapping: (_, _getters) => (boardId) => _getters.getBoardMapping(boardId) || {},
  notMappedFields: (_, _getters) => (boardId) => {
    const mapping = _getters.boardMapping(boardId)
    return keys(
      pickBy(mapping?.mapping, (value, key) =>
        exemptMappingRequirement.includes(key) ? false : isEmpty(value)
      )
    )
  },
  mappedFields: (_, _getters) => (boardId) => {
    const mapping = _getters.boardMapping(boardId)
    return keys(
      pickBy(mapping?.mapping, (value, key) =>
        exemptMappingRequirement.includes(key) ? true : !isEmpty(value)
      )
    )
  },
  getAccessLevel:
    (_, _getters) =>
    ({ boardId, uid }) => {
      const board = _getters.getBoard(boardId)
      const levelRecord = board?.accessLevels?.find((level) => level.uid === uid)

      return levelRecord?.accessLevel || ACCESS_LEVELS_DICT.NO_ACCESS
    },
  boards: (_state) => _state.boards,
  isSampleData: (_state, _getters) => (boardId) => _getters.getBoard(boardId)?.sample,
  mainBoards: (_state) => _state.boards?.filter((board) => board.masterBranch) || [],
  scenarios: (_state) => (boardId) =>
    _state.boards?.filter(
      (board) => !board.masterBranch && board.originalBoardId === boardId && !board?.deletedAt
    ) || [],
  defaultBoard: (_, _getters) => _getters.mainBoards[0],
  accessLevels: (_, _getters) => (boardId) => _getters.getBoard(boardId)?.accessLevels,
  accessLevelsByUid: (_, _getters) => (boardId) => keyBy(_getters.accessLevels(boardId), 'uid'),
  isFetchingBoards: (_state) => _state.isFetching,
  hasLoadedBoards: (_state) => _state.hasLoadedBoards,
  isMainBoard: (_, _getters) => (boardId) => {
    const board = _getters.getBoard(boardId)
    return (board?.originalBoardId ?? board.boardId) === boardId
  },
  getConfigData: (_, _getters) => (boardId) => _getters.getBoard(boardId)?.configData || {}
}

function getActiveFetchForUser(userId) {
  if (!activeFetchAllBoards) return null
  if (activeFetchAllBoards.userId === userId) return activeFetchAllBoards

  // Board state belongs to one user. Prevent the previous user's fetch from committing.
  fetchAllBoardsGeneration++
  activeFetchAllBoards = null
  return null
}

async function fetchBoardsWithRetry(context, userId, requestGeneration) {
  context.commit('setIsFetching', true)
  const maxTries = 2
  for (let count = 0; count <= maxTries; count++) {
    try {
      const boards = await getBoards({ userId, forceTokenRefresh: count > 0 })
      if (requestGeneration !== fetchAllBoardsGeneration) {
        // Do not save boards if this request got invalidated by a user change or store reset (e.g user logout)
        return false
      }

      context.commit('setBoards', boards)
      context.commit('setBoardsLoaded', true)
      return true
    } catch (error) {
      if (count === maxTries) throw error
    }
  }
}

function startBoardFetch(context, userId, force) {
  const requestGeneration = fetchAllBoardsGeneration
  let activeFetch
  const promise = fetchBoardsWithRetry(context, userId, requestGeneration).finally(() => {
    if (requestGeneration === fetchAllBoardsGeneration && activeFetchAllBoards === activeFetch) {
      activeFetchAllBoards = null
      context.commit('setIsFetching', false)
    }
  })

  activeFetch = { userId, force, promise }
  activeFetchAllBoards = activeFetch
  return promise
}

async function requestBoards(context, userId, force) {
  const activeFetch = getActiveFetchForUser(userId)
  if (activeFetch) {
    if (!force) return activeFetch.promise

    // A forced refresh must observe mutations that may have happened while another fetch ran.
    await activeFetch.promise.catch(() => undefined)
    return requestBoards(context, userId, force)
  }

  if (!force && context.state.hasLoadedBoards) return true
  return startBoardFetch(context, userId, force)
}

const actions = {
  async checkBoardAccess(context, { boardId }) {
    try {
      await getBoard({ boardId })
      return true
    } catch (e) {
      // Access not possible
      return false
    }
  },
  fetchAllBoards(context, userId) {
    return requestBoards(context, userId, true)
  },
  fetchAllBoardsIfMissing(context, userId) {
    return requestBoards(context, userId, false)
  },
  async fetchBoardMapping(context, boardId) {
    try {
      // check for mapping for the passed board Id
      let mappingInfo = await getBoardMapping({ boardId })
      if (!mappingInfo) throw new Error('No mapping found')

      context.commit('updateBoardMapping', { boardId, mapping: mappingInfo })
    } catch (error) {
      logger.info(error.message)
    }
  },
  async updateAccessLevel(context, { targetUid, newAccessLevel, boardId }) {
    try {
      //MIGRATION: is this more like POST /board/:boardId to update the document?
      //TODO: do we need to fetch again here or just use the board from store?
      const { accessLevels, owners } = await updateAccessLevel({
        targetUid,
        newAccessLevel,
        boardId
      })

      // update the store because we are not fetching again from the server
      context.commit('updateBoard', {
        boardId,
        key: 'accessLevels',
        value: accessLevels
      })
      context.commit('updateBoard', {
        boardId,
        key: 'owners',
        value: owners
      })
    } catch (err) {
      logger.info('err in updateAccessLevel', err.message)
      throw err
    }
  },
  reset({ commit }) {
    fetchAllBoardsGeneration++
    activeFetchAllBoards = null
    commit('reset')
  },
  updateBoard(context, { boardId, key, value }) {
    context.commit('updateBoard', { boardId, key, value })
  },
  async updateAndSyncBoard(context, { boardId, key, value }) {
    context.commit('updateBoard', { boardId, key, value })
    await context.dispatch('submitBoard', { boardId })
  },
  async submitBoard(context, { boardId }) {
    const targetBoard = find(context.state.boards, (board) => {
      return board.boardId === boardId
    })

    try {
      if (!targetBoard) return

      await updateBoard({
        boardId,
        payload: {
          ...targetBoard
        }
      })
    } catch (err) {
      logger.info('err in submitBoard', err.message)
    }
  },
  async deleteClonedBoard(context, { boardId }) {
    const targetBoard = context.state.boards.find(
      (board) => board.boardId === boardId && !board.masterBranch
    )

    if (!targetBoard) return

    // Delete board, people, compensations, plan_invitations, and plan_logs
    await deleteBoard({ boardId })
  },

  /**
   * Force delete a main board and all associated scenarios/data.
   * Used for dev/testing purposes (e.g., Dayforce simulator reset).
   */
  async forceDeleteMainBoard(context, { boardId }) {
    const targetBoard = context.state.boards.find(
      (board) => board.boardId === boardId && board.masterBranch
    )

    if (!targetBoard) return null

    const result = await deleteMainBoard({ boardId })

    if (result) {
      // Remove the board and all its scenarios from local state
      const updatedBoards = context.state.boards.filter(
        (board) => board.boardId !== boardId && board.originalBoardId !== boardId
      )
      context.commit('setBoards', updatedBoards)
    }

    return result
  },
  async addCollaborators({ commit, getters }, { boardId, employees, setAsAdmin = false }) {
    try {
      const board = getters.getBoard(boardId)
      const localCollaborators = board?.collaborators || []
      const localOwners = board?.owners || []
      const localAccessLevels = board?.accessLevels || []

      const currentBoard = await getBoard({ boardId })
      const currentAccessLevels = currentBoard.accessLevels || []
      const currentCollaborators = currentBoard.collaborators || []
      const currentOwners = currentBoard.owners || []

      const personIds = employees
        .map((employee) => employee.personId)
        .filter((employee) => employee)
      const userIds = employees.map((employee) => employee.uid)
      // for submitted plans add collaborators as admin
      const accessLevels = employees.map((employee) => ({
        uid: employee.uid,
        accessLevel: setAsAdmin ? ACCESS_LEVELS_DICT.ADMIN : employee.accessLevel
      }))

      // Add new collaborators to the end of the list
      const mergedExistingAccessLevels = unionBy(currentAccessLevels, localAccessLevels, 'uid')
      const newAccessLevelsByUid = keyBy(accessLevels, 'uid')
      const updatedAccessLevels = [
        ...mergedExistingAccessLevels.filter((level) => !newAccessLevelsByUid[level.uid]),
        ...accessLevels
      ]
      const updatedCollaborators = union(currentCollaborators, localCollaborators, personIds)
      const updatedOwners = union(currentOwners, localOwners, userIds)

      await updateBoard({
        boardId,
        payload: {
          collaborators: updatedCollaborators,
          owners: updatedOwners,
          accessLevels: updatedAccessLevels
        }
      })

      commit('updateBoard', {
        boardId,
        key: 'collaborators',
        value: updatedCollaborators
      })

      commit('updateBoard', {
        boardId,
        key: 'owners',
        value: updatedOwners
      })

      commit('updateBoard', {
        boardId,
        key: 'accessLevels',
        value: updatedAccessLevels
      })
    } catch (e) {
      logger.info('error while adding collaborator', e.message)
      Sentry.captureException(e)
    }
  },
  async removeCollaborators({ commit, getters }, { boardId, employees }) {
    try {
      const personIds = employees.map((employee) => employee.personId)
      const userIds = employees.map((employee) => employee.uid)

      const board = getters.getBoard(boardId)
      const collaborators =
        board?.collaborators.filter((collaborator) => !personIds.includes(collaborator)) || []
      const owners = board?.owners.filter((owner) => !userIds.includes(owner)) || []
      const accessLevels = board.accessLevels.filter(
        (accessLevel) => !userIds.includes(accessLevel.uid)
      )

      await updateBoard({
        boardId,
        payload: {
          collaborators,
          accessLevels,
          owners
        }
      })

      commit('updateBoard', {
        boardId,
        key: 'collaborators',
        value: collaborators
      })

      commit('updateBoard', {
        boardId,
        key: 'owners',
        value: owners
      })
    } catch (e) {
      logger.info(e.message)
    }
  },
  async toggleSyncWithOrg({ commit, rootGetters }, { boardId, isSyncEnabled }) {
    try {
      commit('updateBoard', { boardId, key: 'isSyncEnabled', value: isSyncEnabled })

      await updateBoard({
        boardId,
        payload: {
          isSyncEnabled,
          updatedBy: rootGetters.uid
        }
      })
    } catch (err) {
      Sentry.captureException(err)
    }
  },
  async addBoardConfig(context, { boardId, configData }) {
    try {
      const board = context.getters.getBoard(boardId)
      const newConfigs = []
      if (!board) return
      if (!configData) return

      configData.id = crypto.randomUUID()

      if (board.configData && typeof board.configData === 'object' && !isArray(board.configData)) {
        logger.info('has object')
        //upgrade old field
        const existingConfig = board.configData
        existingConfig.name = 'Saved config'
        existingConfig.id = crypto.randomUUID()

        newConfigs.push(existingConfig)
      } else if (board.configData && isArray(board.configData)) {
        newConfigs.push(...board.configData)
      }

      newConfigs.push(configData)

      await context.dispatch('updateAndSyncBoard', {
        boardId,
        key: 'configData',
        value: newConfigs
      })

      return configData
    } catch (err) {
      Sentry.captureException(err)
    }
  },
  async removeBoardConfig(context, { boardId, configId }) {
    try {
      const board = context.getters.getBoard(boardId)
      if (!board) return

      const newConfigs = board.configData.filter((config) => config.id !== configId)

      await context.dispatch('updateAndSyncBoard', {
        boardId,
        key: 'configData',
        value: newConfigs
      })
    } catch (err) {
      Sentry.captureException(err)
    }
  },
  async updateBoardName({ commit }, { boardId, boardName }) {
    await updateBoard({
      boardId,
      payload: {
        boardName,
        companyName: boardName
      }
    })

    commit('updateBoard', {
      boardId,
      key: 'boardName',
      value: boardName
    })

    commit('updateBoard', {
      boardId,
      key: 'companyName',
      value: boardName
    })
  },
  setColour(context, { boardId, content, filter, colour }) {
    context.commit('SET_COLOUR', { boardId, content, filter, colour })
  },
  async updateTheme(context, { boardId, theme }) {
    const boardData = await getBoard({
      boardId
    })
    context.commit('SET_THEME', { boardId, theme })
    await updateBoard({ boardId, payload: { ...boardData, theme } })
  }
}

export default {
  state,
  mutations,
  getters,
  actions,
  modules: {}
}
